import { useState } from 'react';
import {
  CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronRight,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { chatTheme as th } from '../../theme/chat';

function formatCurrency(value) {
  if (value === null || value === undefined) return '--';
  const num = typeof value === 'number' ? value : Number(value);
  if (isNaN(num)) return String(value);
  const formatted = Math.abs(num).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (num < 0) return `-$${formatted}`;
  return `$${formatted}`;
}

function StatusBadge({ status }) {
  if (status === 'pass') {
    return (
      <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border', th.reconciliation.pass)}>
        <CheckCircle2 size={10} /> PASS
      </span>
    );
  }
  if (status === 'fail') {
    return (
      <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border', th.reconciliation.fail)}>
        <XCircle size={10} /> FAIL
      </span>
    );
  }
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border', th.reconciliation.error)}>
      <AlertTriangle size={10} /> {(status || 'ERROR').toUpperCase()}
    </span>
  );
}

function ComparisonRow({ item }) {
  const variance = item.variance ?? 0;
  const isMatch = item.match !== false && Math.abs(variance) < 0.02;
  const isClose = !isMatch && Math.abs(variance) < 1.0;

  return (
    <tr className={cn(
      'border-b border-zinc-100 text-xs',
      isMatch && th.reconciliation.comparison.matchRow,
      !isMatch && !isClose && th.reconciliation.comparison.failRow,
      isClose && 'bg-amber-50/50',
    )}>
      <td className={cn('px-3 py-1.5', th.reconciliation.comparison.cellText)}>{item.label || item.company_name || item.rf_id || '--'}</td>
      <td className={cn('px-3 py-1.5 text-right', th.reconciliation.comparison.cellText)}>{formatCurrency(item.cube_value)}</td>
      <td className={cn('px-3 py-1.5 text-right', th.reconciliation.comparison.cellText)}>{formatCurrency(item.qb_value)}</td>
      <td className={cn(
        'px-3 py-1.5 text-right tabular-nums font-medium',
        isMatch ? 'text-emerald-600' : isClose ? 'text-amber-600' : 'text-red-600',
      )}>
        {formatCurrency(variance)}
      </td>
      <td className="px-3 py-1.5 text-center">
        {isMatch ? (
          <CheckCircle2 size={12} className="text-emerald-500 inline" />
        ) : (
          <XCircle size={12} className="text-red-500 inline" />
        )}
      </td>
    </tr>
  );
}

function TierSection({ tier, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded ?? tier.status === 'fail');

  const levelLabels = {
    total: 'Total',
    section: 'Section',
    account: `Account${tier.section ? ` (${tier.section})` : ''}`,
    dimension: `Dimension${tier.dimension ? ` — ${tier.dimension}` : ''}`,
    company: 'Company',
  };

  const label = levelLabels[tier.level] || tier.level;
  const comparisons = tier.comparisons || [];
  const failCount = comparisons.filter(c => c.match === false || (c.variance && Math.abs(c.variance) >= 0.02)).length;

  return (
    <div className="border border-zinc-200 rounded-lg overflow-hidden mb-2">
      {/* Tier header */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2 bg-zinc-50 hover:bg-zinc-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown size={14} className="text-zinc-400" />
          ) : (
            <ChevronRight size={14} className="text-zinc-400" />
          )}
          <span className={th.reconciliation.tierHeader}>{label}</span>
          <StatusBadge status={tier.status} />
        </div>
        <span className="text-[10px] text-zinc-400">
          {comparisons.length} comparison{comparisons.length !== 1 ? 's' : ''}
          {failCount > 0 && <span className="text-red-600 ml-1">({failCount} failed)</span>}
        </span>
      </button>

      {/* Tier body */}
      {expanded && comparisons.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className={cn(th.reconciliation.comparison.headerBg, 'border-t border-zinc-200')}>
                <th className={cn('text-left px-3 py-1.5 font-semibold', th.reconciliation.comparison.headerText)}>Label</th>
                <th className={cn('text-right px-3 py-1.5 font-semibold', th.reconciliation.comparison.headerText)}>Cube</th>
                <th className={cn('text-right px-3 py-1.5 font-semibold', th.reconciliation.comparison.headerText)}>QB</th>
                <th className={cn('text-right px-3 py-1.5 font-semibold', th.reconciliation.comparison.headerText)}>Variance</th>
                <th className={cn('text-center px-3 py-1.5 font-semibold w-10', th.reconciliation.comparison.headerText)}></th>
              </tr>
            </thead>
            <tbody>
              {comparisons.map((item, i) => (
                <ComparisonRow key={i} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function ReconciliationPanel({ result }) {
  const [collapsed, setCollapsed] = useState(false);

  if (!result || !result.tiers?.length) return null;

  const tiers = result.tiers || [];
  const totalVariance = result.total_variance ?? 0;

  return (
    <div className="border-t border-zinc-200 bg-white flex-shrink-0">
      {/* Panel header */}
      <button
        onClick={() => setCollapsed(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-zinc-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-zinc-800">Reconciliation</span>
          <StatusBadge status={result.status} />
          {totalVariance !== 0 && (
            <span className={cn(
              'text-[11px] font-medium tabular-nums',
              totalVariance > 0 ? 'text-red-600' : 'text-red-600',
            )}>
              Total variance: {formatCurrency(totalVariance)}
            </span>
          )}
        </div>
        {collapsed ? (
          <ChevronRight size={14} className="text-zinc-400" />
        ) : (
          <ChevronDown size={14} className="text-zinc-400" />
        )}
      </button>

      {/* Tiers */}
      {!collapsed && (
        <div className="px-4 pb-3 max-h-[400px] overflow-y-auto">
          {tiers.map((tier, i) => (
            <TierSection key={`${tier.level}-${i}`} tier={tier} defaultExpanded={i === 0} />
          ))}
          {result.executed_at && (
            <div className="text-[10px] text-zinc-400 mt-2 text-right">
              Last run: {new Date(result.executed_at).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
